import { CELL } from "../constants.js";
import {
  DR,
  DC,
  isSafeFromFire,
  isAtBoundary,
} from "./solverUtils.js";

export function iddfsSolve(grid, rows, cols, start, fireDist, fireRate = 1) {
  const size = rows * cols;
  let maxSurvival = -1;
  let exitNode = -1;
  let limit = 0;

  const trace = new Int16Array(size).fill(-1);
  const gScore = new Int16Array(size).fill(0x7fff);

  // Stack (Size N)
  const stIdx = new Int16Array(size * 2);
  const stDist = new Int16Array(size * 2);
  const depthTrace = new Int16Array(size + 1).fill(-1);

  // Visualization History (Linear Arrays)
  const visIdx = [];
  const visDist = [];
  const frontIdx = [];
  const frontTick = [];
  const visParentIdx = []; // Stores parent NodeID

  let bestPath = [start];
  let lastVisCount = 0;

  while (limit <= size) {
    // Reset state for this iteration
    gScore.fill(0x7fff);

    let fullyExplored = true;
    let ptr = 0;

    stIdx[ptr] = start;
    stDist[ptr] = 0;
    ptr++;

    gScore[start] = 0;
    trace[start] = -1;
    depthTrace[0] = start;

    // Inner Depth-First Search loop
    while (ptr > 0) {
      ptr--;
      const cur = stIdx[ptr];
      const dist = stDist[ptr];

      visIdx.push(cur);
      visDist.push(dist);
      visParentIdx.push(trace[cur]); // Snapshot parent

      depthTrace[dist] = cur;

      // Track best node
      if (fireDist[cur] > maxSurvival) {
        maxSurvival = fireDist[cur];
        bestPath = new Int16Array(dist + 1);
        for (let i = 0; i <= dist; i++) {
          bestPath[i] = depthTrace[i];
        }
      }

      // Project 1D array index back to 2D coordinates
      const r = Math.floor(cur / cols);
      const c = cur % cols;

      // Exit condition met
      if (isAtBoundary(r, c, rows, cols)) {
        exitNode = cur;
        break;
      }

      // Cut off exploration if current branch depth hits the IDDFS limit
      if (dist >= limit) {
        fullyExplored = false;
        continue;
      }

      // Evaluate orthogonal neighbors using the optimal default order of DR/DC
      for (let i = 0; i < 4; i++) {
        const nr = r + DR[i];
        const nc = c + DC[i];

        // Bounds checking
        if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;

        const next = nr * cols + nc;
        if (grid[next] & CELL.WALL) continue;

        const nextDist = dist + 1;

        if (nextDist < gScore[next] && isSafeFromFire(nextDist, fireDist[next], fireRate)) {
          gScore[next] = nextDist;
          trace[next] = cur;

          if (ptr < stIdx.length) {
            stIdx[ptr] = next;
            stDist[ptr] = nextDist;
            ptr++;
          }

          fullyExplored = false;

          frontIdx.push(next);
          frontTick.push(visIdx.length - 1);
        }
      }
    }

    if (exitNode !== -1) break;
    if (visIdx.length === lastVisCount) break;
    lastVisCount = visIdx.length;
    if (fullyExplored) break;

    limit++;
  }

  // Final Path
  let path = [];
  if (exitNode !== -1) {
    for(let i=0; i<=limit; i++) {
        if(depthTrace[i] === exitNode) {
             for(let j=0; j<=i; j++) path.push(depthTrace[j]);
             break;
        }
    }
  } else {
    for (let i = 0; i < bestPath.length; i++) {
      path.push(bestPath[i]);
    }
  }

  // Visualization Data
  const visitedNodesInOrder = new Array(visIdx.length);
  for (let i = 0; i < visIdx.length; i++) {
    visitedNodesInOrder[i] = {
      idx: visIdx[i],
      d: visDist[i],
      parentIdx: visParentIdx[i]
    };
  }

  const frontierNodesInOrder = new Array(frontIdx.length);
  for (let i = 0; i < frontIdx.length; i++) {
    frontierNodesInOrder[i] = {
      idx: frontIdx[i],
      discoveredAtTick: frontTick[i],
    };
  }

  return {
    visitedNodesInOrder,
    frontierNodesInOrder,
    path,
    trace,
    isWin: exitNode !== -1,
  };
}
